<template>
	<div id="vehicles-table">
		<v-row class="pl-4 pt-4">
			<v-col cols="2">
				<v-autocomplete
					v-model="selectedOrg"
					:items="orgName"
					label="Organisations"
					item-text="name"
					outlined
					@change="fetchOrgNodes"
				/>
			</v-col>

			<v-col cols="2">
				<v-autocomplete
					v-model="selectedGroup"
					:items="groups"
					item-text="name"
					item-value="id"
					label="Group"
					outlined
				/>
			</v-col>

			<v-col cols="2" class="pt-8">
				<v-text-field
					v-model="search"
					append-icon="mdi-magnify"
					label="Search"
					single-line
					hide-details
				></v-text-field>
			</v-col>
			<v-spacer></v-spacer>
			<v-col cols="2" class="d-flex justify-end pr-6">
				<v-icon 
					@click="refreshTable"
				>
					mdi-refresh
				</v-icon>

				<v-icon @click="downloadFile">
					mdi-download
				</v-icon>
			</v-col>
		</v-row>
		

		<v-data-table
			v-model="selectedItems"
			show-select
			:headers="fiteredTableHeaders"
			:items="vehicles"
			:loading="loading"
			:hide-default-footer="true"
			:items-per-page="-1"
			class="elevation-1 admin-table"
		>
			<template v-slot:progress>
				<v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
			</template>

			<template v-slot:item.status="{ item }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-avatar
							:color="statusColor(vehicleStatus[item.id])"
							size="20"
							v-bind="attrs"
							v-on="on"
						>
						</v-avatar>
					</template>
					<span>{{ vehicleStatus[item.id] || 'Unknown' }}</span>
				</v-tooltip>
			</template>

			<template v-slot:item.last_seen="{ item }">
				<v-tooltip 
					v-if="devicePresenceInfo(item)"
					right 
					content-class="info-popup-container"
				>
					<template v-slot:activator="{ on, attrs }">
						<div 
							v-bind="attrs"
							v-on="on"
						>
							{{ item.last_seen }}
						</div>
					</template>
					<DevicePresenceInfo 
						:info="devicePresenceInfo(item)"
					/>
				</v-tooltip>
				<div v-else>
					{{ item.last_seen }}
				</div>
			</template>

			<template v-slot:item.action="{item}">
				<div class="reports-action-launch" @click="launchRowAction">
					<v-icon>mdi-dots-vertical</v-icon>
					<ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
						<ActionItem 
							:disabled="!$can('updateVehicle') || selectedCountGreaterThanOne"
							label="Edit Vehicle"
							icon="mdi-pencil"
							@click.native="toggleConfigComponent(item, 'vehicle')"/>
	
						<ActionItem 
							label="Move to Org"
							icon="mdi-arrow-right"
							@click.native="toggleMoveComp(item)"/>

						<ActionItem 
							label="Move to Group"
							icon="mdi-arrow-right"
							@click.native="toggleMoveGroupComp(item)"/>

						<ActionItem 
							:disabled="!$can('viewDeviceInfo') || selectedCountGreaterThanOne"
							icon="mdi-laptop"
							label="Device Info"
							@click.native="toggleConfigComponent(item, 'device')"/>

						<ActionItem 
							:disabled="!$can('editAlertConfig') || selectedCountGreaterThanOne"
							icon="mdi-bell"
							label="Alert Config"
							@click.native="toggleConfigComponent(item, 'alert')"/>

						<ActionItem 
							:disabled="!$can('viewDeviceInfo') || selectedCountGreaterThanOne"
							label="Device Config"
							icon="mdi-settings"
							@click.native="toggleDeviceConfig(item)"/>

						<ActionItem 
							:disabled="selectedCountGreaterThanOne"
							label="Retrieve Logs"
							icon="mdi-file"
							@click.native="toggleLogComp(item)"/>

						<ActionItem 
							:disabled="selectedCountGreaterThanOne"
							label="Reboot Device"
							icon="mdi-power"
							@click.native="toggleReboot(item)"/>
						
						<ActionItem 
							:disabled="!$can('deleteVehicle') || selectedCountGreaterThanOne"
							label="Delete"
							icon="mdi-delete"
							@click.native="toggleDeletePrompt(item)"/>

						<ActionItem 
							:disabled="selectedCountGreaterThanOne"
							label="Format Disk"
							icon="mdi-harddisk"
							@click.native="toggleFormat(item)"/>

						<ShowOnMap :disabled="selectedCountGreaterThanOne" :org="orgId" :device="item.devices && item.devices.length ? item.devices[0].device_id : false" />

						<ActionItem
							:disabled="!$can('createFirmwareUpdateJob') && !$can('createDeviceConfigJob') && !$can('createDeviceInstallJob') || selectedCountGreaterThanOne"
							label="Create Job"
							icon="mdi-clipboard-list"
							@click.native="toggleJobCreation(item)"/>
					</ActionContainer>
				</div>
			</template>
	
			<template v-slot:item.notes="{ item }">
        <v-layout justify-center>
          <v-icon @click.native="toggleConfigComponent(item, 'vehicle')">
            mdi-file
          </v-icon>
        </v-layout>
      </template>
		</v-data-table>

		<Pagination
			:pagination-info="paginationInfo"
			:show-row-option="true"
			@updatedPage="fetchVehicleList"
		/>

		<DownloadLog 
			:display="showLogs"
			:device="logItem" 
			:org="orgId"
			@closeLogComp="closeLogComp"
		/>

		<ConfigPanel 
			v-if="displayConfig"
			:items="selectedItems"
			:groups="vehicleGroups"
			:vehicle-types="vehiType"
			:multi-device="false"
			:org="orgId"
			:nodes="nodes"
			:selected-contacts="selectedContacts"
			@toggleModal="toggleModal"
			@refreshData="refreshData"
		/>

		<InfoContainer
      :display="displayVehicleInfo"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="closeInfoContainer('vehicle')"
    >
      <pvcam-wc-vehicle-config 
        :items="JSON.stringify(selectedItems)"
        :groups="JSON.stringify(vehicleGroups)"
        :org="JSON.stringify(orgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify(nodes.root_node)"
        :multi-device="JSON.stringify(multiDevices)"
				:admin="true"
				:translate="JSON.stringify(false)"
        @showSelectContacts="handleSelection"
        @refreshData="refreshData"
      />
    </InfoContainer>

		<!-- <InfoContainer
      :display="displayDeviceInfo"
      :width="1000"
      :panel-heading="panelHeading"
      @closeModal="closeInfoContainer('device')"
    >
      <pvcam-wc-device-info
        :org="JSON.stringify(orgId)"
        :items="JSON.stringify(selectedItems)"
      />
    </InfoContainer> -->

		<DeviceInfo 
			v-if="displayDeviceInfo"
      :display-device-info="displayDeviceInfo"
      :device-id="selectedVehicleDeviceId"
			:vehicle="selectedItems[0]"
      :org="orgId"
      @close="displayDeviceInfo = false"
    />

		<InfoContainer
      :display="displayAlertConfig"
      :width="600"
      :panel-heading="panelHeading"
      @closeModal="closeInfoContainer('alert')"
    >
      <pvcam-wc-device-alert-config
        :items="JSON.stringify(selectedItems)"
        :groups="JSON.stringify(vehicleGroups)"
        :org="JSON.stringify(orgId)"
        :vehicle-types="JSON.stringify(vehiType)"
        :nodes="JSON.stringify(nodes.root_node)"
        :multi-config="JSON.stringify(displayMultiConfig)"
        :selected-contacts="JSON.stringify(selectedContacts)"
        :user-info="JSON.stringify({})"
				:admin-user="true"
        @showSelectContacts="handleSelection"
        @refreshData="refreshData"
      />
    </InfoContainer>

		<DeviceConfig 
			v-if="displayDeviceConfig"
			:item="selectedDevice"
			:org="orgId"
			@closeDeviceConfig="closeInfoContainer('deviceConfig')"/>

		<Prompt
			:display="deleteLogConfirm"
			:close-target="'closeDelete'"
			:submit-target="'submitDeleteLog'"
			:title="'Delete Vehicle'"
			:message="deleteMessage"
			:state="deleteLogState" 
			:res="deleteLogRes"
			@closeDelete="toggleDeletePrompt"
			@submitDeleteLog="deleteItem"
		/>

		<Contacts 
			v-if="orgId"
      v-model="shared" 
			:org-id="orgId"
      :show="showContacts"  
      @close="showContacts = false"
      @input="handleSelection"
		/>

		<DeviceFormat 
			v-if="displayFormat"
			:device="formatItem"
			:org="orgId"
			@close="toggleFormat(null)"
		/>
		<DeviceReboot 
			v-if="displayReboot"
			:org="orgId"
			:device="rebootDevice"
			@close="toggleReboot(null)"
		/>

		<add-job 
			:key="refreshAddJob"
			:open="jobVisibility" 
			:mode="mode" 
			:job-item="jobItem"
			@exit="toggleJobCreation(null)"
		/>

		<MoveVehicleBetweenOrg
			v-if="displayMoveVehicle"
			:orgs="orgs"
			:vehicle-list="selectedItems"
			@close="toggleMoveComp(null)"
			@success="updateVehicleList"
		/>

		<MoveVehicleToNewGroup
      v-if="displayMoveGroup"
      :groups="groups"
      :vehicle-list="selectedItems"
			:org-id="orgId"
      @close="toggleMoveGroupComp(null)"
      @success="fetchVehicleList"
    />
	</div>
</template>

<script>
	import { mapState, mapMutations, mapActions } from "vuex";
	import { AmplifyEventBus } from "aws-amplify-vue";
	import ShowOnMap from '@/components/general/menu/ShowOnMap.vue'
	import {
		GetOrgVehicles,
		UpdateOrgVehicle,
		DeleteOrgVehicle,
		GetOrgTree,
		UnassignDeviceFromVehicle,
		GetOrgDevices,
    ListAccessibleOrgs,
		GetOrgVehiclesByNode,
		GetOrgDevicesByNode,
		SearchOrgVehicles
	} from "@/services/API.js";

	import { CreateDVRFileRequest } from "@/services/file-retrieval.js";
	import moment from "moment";
	import xlsx from 'xlsx';

	// import DeleteVehicle from '../components/DeleteVehicle.vue';
	import ConfigPanel from '@/views/OrgVehicles/Sections/ConfigPanel.vue';
	import Contacts from '@/components/contacts/ContactsModal.vue';
	import DeviceConfig from '@/components/deviceConfig/config.vue';
	import DownloadLog from '@/views/OrgVehicles/components/DownloadLog.vue';
	import ActionContainer from '@/components/general/ActionContainer.vue';
	import ActionItem from '@/components/general/ActionItem.vue';
	import MoveVehicleToNewGroup from "@/components/general/MoveVehicleToNewGroup";
	import DeviceInfo from '@/components/deviceInfo/deviceInfo.vue';

	import { getColor, findDefaultDevice } from '@/utils'

	import { presence } from '@/mixins/presence'


	import Prompt from '@/components/general/Prompt.vue';
	import DeviceFormat from '@/components/general/DeviceFormat.vue';
	import DeviceReboot from '@/components/general/DeviceReboot.vue';
	import DevicePresenceInfo from '@/components/general/DevicePresenceInfo.vue';
	import AddJob from "@/views/Jobs/Sections/AddJob.vue";
	import InfoContainer from '@/components/general/InfoContainer.vue';
	import MoveVehicleBetweenOrg from '@/components/general/MoveVehicleBetweenOrg.vue'
	import Pagination from "@/components/general/Pagination.vue"

	import { Logger } from "aws-amplify";
	const logger = new Logger("org vehicles");

	export default {
    name: 'Vehicles',

		components: {
			ConfigPanel,
			Contacts,
			DeviceConfig,
			DownloadLog,
			ActionContainer,
			ActionItem,
			Prompt,
			DeviceFormat,
			DeviceReboot,
			DevicePresenceInfo,
			ShowOnMap,
			AddJob,
			InfoContainer,
			MoveVehicleBetweenOrg,
			Pagination,
			MoveVehicleToNewGroup,
			DeviceInfo
		},

		mixins: [ presence ],

		props: {
			update: {
				type: Number,
				default: 0
			}
		},

		data: () => ({
			showLogs: false,
			search: '',
			headers: [
				{
          text: 'Status',
          value: 'status',
          align: 'left'
        },
				{
					text: 'Last Seen',
					value: 'last_seen',
					align: 'left'
				},
				{
					text: 'Install Tag',
					value: 'install_tag',
					align: 'left'
				},
       	{
          text: 'Name',
          value: 'vehicle_name',
          align: 'left'
        },
        {
          text: 'Type',
          value: 'vehicle_type',
          align: 'left'
        },
        {
          text: 'Group',
          value: 'group',
          align: 'left'
        },
        {
          text: 'Make',
          value: 'vehicle_make',
          align: 'left'
        },
        {
          text: 'Model',
          value: 'vehicle_model',
          align: 'left'
        },
        {
          text: 'Fuel (L)',
          value: 'vehicle_fuel',
          align: 'left'
        },
        {
          text: 'Displacement',
          value: 'vehicle_displacement',
          align: 'left'
        },
        {
          text: 'Year',
          value: 'vehicle_year',
          align: 'left'
        },
				{
          text: 'VIN',
          value: 'vehicle_vin',
          align: 'left'
        },
        {
          text: 'Notes',
          value: 'notes',
          align: 'left'
        },
				{ 
					text: "Actions", 
					value: "action", 
					align: 'left'
				}
      ],
			vehicles: [],
			loading: false,
			deletingItem: false,
			loader: false,
			noresult: false,
			dialog: false,
			editedIndex: -1,
			editedItem: {
				vehicle_name: "",
				vehicle_type: ""
			},
			defaultItem: {
				vehicle_name: "",
				vehicle_type: ""
			},
			displayConfig: false,
			selectedItems: [],
			vehicleGroups: [],
			nodes: [],
			selectedContacts: {},
			showContacts: false,
			shared: [],
			selectedDevice: null,
			displayDeviceConfig: false,
			date: moment().format("YYYY-MM-DD HH:mm:ss"),
			logItem: {},
			itemForDeletion: {},
			showDelete: false,
			deleteComplete: false,
			deleteLogConfirm: false,
      deleteLogState: false,
      deleteLogRes: null,
      deleteTarget: null,

			displayFormat: false,
			formatItem: null,
			
			displayReboot: false,
			rebootDevice: null,

			devices: [],

      // orgs: [],
      selectedOrg: '',
			selectedGroup: '',

			mode: "new",
			jobVisibility: false,
      jobItem: null,
      refreshAddJob: 0,

			displayVehicleInfo: false,
			displayDeviceInfo: false,
			displayAlertConfig: false,
			multiDevices: false,
			displayMultiConfig: false,
			displayMoveVehicle: false,

			paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        rowsPerPage: 10
      },
			page: 1,

			groups: [],

			searchTermTimeout: false,
      lastSearchAbove3Characters: false, 

			displayMoveGroup: false
		}),

		computed: {
			...mapState([
				'vehiType',
				'orgs'
			]),

			...mapState('devicePresence', [
				'vehicleStatus', 'devicesMap'
			]),

			// returns a key value map of org node id => org node label
			nodesMap() {
				let map = {}
				let reduce = (node) => {
					if(node && node.metadata && node.metadata.label) {
						map[node.id] = node.metadata.label

						if(node.nodes && node.nodes.length) {
							node.nodes.forEach(subNode => {
								reduce(subNode)
							})  
						}
					}
				}

				if(this.nodes && this.nodes.root_node) {
					reduce(this.nodes.root_node)
				}

				return map
			},

			fiteredTableHeaders () {
				return this.headers.filter(i => !this.hideHeader[i.value])
			},

			hideHeader () {
				return {
					status: !this.$can('getVehiclePresence'),
					last_seen: !this.$can('getVehiclePresence'),
				}
			},

			deleteMessage() {
				if (this.deleteTarget) {
					return this.deleteTarget.devices.length > 0 ? 
						'This vehicle has an existing device assigned. If you proceed then this device will be unassigned.'
						:
						`Are you sure you wish to delete vehicle : ${this.deleteTarget.vehicle_name}?`;
				}
				else {
					return '';
				}
			},

      orgName() {
      	const orgs = this.orgs.map((org) => ({
					name: org.name
				}));

        return orgs;
      },

      orgId() {
        return this.retrunOrgID(this.selectedOrg);
      },
			
			panelHeading() {
				return this.selectedItems.length > 0 
					? this.selectedItems.length > 1 ? 'Multiple Vehicles Selected' : this.selectedItems[0].vehicle_name
					:
					'';
			},
			
			selectedCountGreaterThanOne() {
				return this.selectedItems.length > 1 ? true : false 
			},

			vehiclesFilteredByGroup() {
				if (this.selectedGroup == 'All Groups') return this.vehicles
				return this.vehicles.filter(vehicle => vehicle.group == this.selectedGroup)
			},
			
			availableGroups() {
				const availableGroups = ['All Groups'].concat(this.vehicleGroups)

				return availableGroups
			},

			selectedVehicleDeviceId() {
				const vehicle = this.selectedItems.length > 0 ? this.selectedItems[0] : null
				const devices = Object.hasOwn(vehicle, 'devices') && vehicle.devices.length > 0 ? vehicle.devices : []
				const defaultDevice = devices.length > 0 ? devices.find(device => device.is_defaultdevice == true) : null
				const deviceId = defaultDevice ? defaultDevice.device_id : ''

				return deviceId
			}
		},

		watch: {
			dialog(val) {
				val || this.close();
			},

			update(val) {
				val && this.reload();
			},


			selectedOrg(val) {
				const id = this.retrunOrgID(val);
				logger.info(id)
				// TODO: will need to look at this when sorting out vehicle presence re-implementation for RMG
				// this.setOrgToWatch(id);
				this.selectedItems = []
			},

			selectedGroup() {
				this.fetchVehicleList()
			},

			search(val) {
				if (this.searchTermTimeout) {
					clearTimeout(this.searchTermTimeout)
				}
				
				// if our string is above two characters, lets set a timeout to load the vehicles with the new search term
				if(val.length > 2) {
					this.searchTermTimeout = setTimeout(() => {
						this.searchVehicles()
						this.lastSearchAbove3Characters = true
						this.searchTermTimeout = false
					}, 1500)
				
				// if the user has dropped below 3 characters in the search, we have to search without a search term
				} else if(this.lastSearchAbove3Characters) {
						this.searchTermTimeout = setTimeout(() => {
						this.searchVehicles()
						this.lastSearchAbove3Characters = false
						this.searchTermTimeout = false
					}, 1500)
				}
			}
		},

		beforeMount() {
			this.getVehiType();
		},

		mounted() {
			window.EventBus.$on("wcToggleContacts", this.handleToggleContacts);
			window.EventBus.$on("showSelectContacts", this.handleContactSelection);
			window.EventBus.$on("refreshData", this.refreshData);
			
			this.getOrgs();

			window.$translate = (section, value) => {
				logger.info('translate', section, value)
			}
		},

		methods: {
			refreshTable() {
				if(this.search.length > 0) {
					logger.info('refresh search term')
					this.searchVehicles()
				}
				else {
					this.fetchVehicleList(1, 20)
				}
				
			},


			...mapActions(["getOrgs", "getVehiType"]),

			...mapMutations("presence", [
				"setOrgToWatch"
			]),

			toggleMoveGroupComp(item) {
				this.displayMoveGroup = !this.displayMoveGroup

				if(item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems.push(item);
					}
				} else {
					this.selectedItems = []
				}
			},

			async searchVehicles() {
				this.loading = true
				this.vehicles = []

			
				const search = await SearchOrgVehicles(this.orgId, this.selectedGroup, this.search)

				if(Object.hasOwn(search, 'Vehicles') && Object.hasOwn(search, 'page_num') && Object.hasOwn(search, 'total_pages')) {
					this.paginationInfo.pageNum = search.page_num;
					this.paginationInfo.pageCount = search.total_pages;
					
					// We need to find the closest option of rows per page from the set list of pagination page size select option will break :(
					const rowOptions = [
						20,
						50,
						100,
						200
					]
					const targetPageSize = search.page_size
					const output = rowOptions.reduce((prev, curr) => Math.abs(curr - targetPageSize) < Math.abs(prev - targetPageSize) ? curr : prev);

					this.paginationInfo.rowsPerPage = output
				}
				
				// load the device presence and subscribe to websocket updates for the search
				let presence = await this.$loadPresence(this.orgId, search.Vehicles)
				logger.debug('presence loaded', presence)

				this.vehicles = this.buildTableObj(search.Vehicles);
				this.mapStatusToVehicle();
				this.loading = false;

				logger.info('search...', search)
			},

			async reload() {
				this.loading = true;
				this.vehicles = [];
				this.selectedGroup = '';
				this.manualRefresh();
				let vehicles = false;
				let devices = false;

				// create promises to handle both the request sets we need to make to populate the view
				const vehiclesPromise = new Promise(async res => {
					vehicles = await GetOrgVehicles(this.orgId, true)
					res(true)
				})

				const devicesPromise = new Promise(async res => {
					devices = await this.batchDeviceRequest(this.orgId)
					res(true)
				})
				
				// wait for both requests to finish
				await Promise.all([vehiclesPromise, devicesPromise])

				if(devices) {
					this.devices = devices.Devices;
				}
			        
        if (vehicles && vehicles.length > 0) {
          this.vehicles = this.buildTableObj(vehicles);
					
					this.vehicleGroups = this.flattenOrgTree([this.nodes.root_node]);
					
          this.mapStatusToVehicle();
        } else {
          this.noresult = true;
        }

				this.loading = false;
			},

			async fetchVehicleList(page = 1, rows = 20) {
				if(!this.orgId || !this.selectedGroup || this.selectedGroup == '' || this.orgId == '') return
				logger.info('fetchVehicleList', page, rows)
				this.loading = true
				this.vehicles = []

				try {
					const vehicles = await GetOrgVehiclesByNode(this.orgId, this.selectedGroup, page, rows)
					const devices = await GetOrgDevicesByNode(this.orgId, this.selectedGroup, page, rows)

					if(vehicles && Object.hasOwn(vehicles, 'Vehicles') && devices && Object.hasOwn(devices, 'Devices')) {
						// load the device presence and subscribe to websocket updates for the search
						let presence = await this.$loadPresence(this.orgId, vehicles.Vehicles)
						logger.debug('presence loaded', presence)

						this.devices = devices.Devices
						this.vehicles = this.buildTableObj(vehicles.Vehicles);

						this.paginationInfo.pageNum = vehicles.page_num;
						this.paginationInfo.pageCount = vehicles.total_pages;
						
						// We need to find the closest option of rows per page from the set list of pagination page size select option will break :(
						const rowOptions = [
							20,
							50,
							100,
							200
						]
						const targetPageSize = vehicles.page_size
						const output = rowOptions.reduce((prev, curr) => Math.abs(curr - targetPageSize) < Math.abs(prev - targetPageSize) ? curr : prev);

						this.paginationInfo.rowsPerPage = output

						this.mapStatusToVehicle();
						this.loading = false
					}
				}
				catch (error) {
					this.loading = false
					throw new Error('An error occurred while requesting vehicles list')
				}
			},

			updateVehicleList() {
				this.displayMoveVehicle = false

				const newVehicles = this.vehicles.filter(vehicle => 
					this.selectedItems.every(selectedVehicle => selectedVehicle.id !== vehicle.id))

				this.vehicles = newVehicles
				this.selectedItems = []
			},

			closeInfoContainer(display) {
				this.selectedItems = []

				switch (display) {
					case 'vehicle': {
						this.displayVehicleInfo = !this.displayVehicleInfo;

						break;
					}
					case 'device': {
						this.displayDeviceInfo = !this.displayDeviceInfo;

						break;
					}
					case 'alert': {
						this.displayAlertConfig = !this.displayAlertConfig;

						break;
					}
					case 'deviceConfig': {
						this.displayDeviceConfig = !this.displayDeviceConfig;

						break;
					}
				}
			},

			toggleJobCreation(item) {
				const defaultDevice = findDefaultDevice(item);
				if(item) {
					this.selectedItems = [item]
				} else {
					this.selectedItems = []
				}
				this.refreshAddJob++;
				this.jobVisibility = !this.jobVisibility;

				if(!defaultDevice) return;
		
        this.jobItem = item;
			},

			toggleMoveComp(item) {
				this.displayMoveVehicle = !this.displayMoveVehicle

				if(item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems.push(item);
					}
				} else {
					this.selectedItems = []
				}
			},

			toggleConfigComponent(item, display) {
				logger.info(item, display)

				const selectedAmount = this.selectedItems.length;
				const displayBoolMap = {
					vehicle: this.displayVehicleInfo,
					device: this.displayDeviceInfo,
					alert: this.displayAlertConfig
				}

				if (item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems = [item];
					}
				} else {
					// Clear selection for single select
					if (selectedAmount === 1 && displayBoolMap[display]) {
						this.selectedItems = [];
					}
				}

				switch (display) {
					case 'vehicle': {
						this.displayVehicleInfo = !this.displayVehicleInfo;

						break;
					}
					case 'device': {
						this.displayDeviceInfo = !this.displayDeviceInfo;

						break;
					}
					case 'alert': {
						this.displayAlertConfig = !this.displayAlertConfig;

						break;
					}
				}
			},

			
      getAllOrgs() {
        ListAccessibleOrgs()
        .then(res => {
          this.orgs = res.sort((a, b) => a.name.localeCompare(b.name));
        })
        .catch(err => {
          this.errors = err;
        });
      },

      retrunOrgID(name) {
        let org = this.orgs.find(org => {
          return org.name == name;
        });

        return org ? org.id : '';
      },

			mapStatusToVehicle() {
				this.vehicles.forEach(vehicle => {
					let device = vehicle.devices.find(device => device.is_defaultdevice == true);

					vehicle.status = this.vehicleStatus[vehicle.id] ? this.vehicleStatus[vehicle.id] : 'UNKNOWN';
					vehicle.last_seen = device && this.devicesMap[device.device_id] ? moment(this.devicesMap[device.device_id]['last_seen_local_time']).format('YYYY MMM DD HH:mm:ss') : 'Unknown';
				});
			},

			devicePresenceInfo(data) {
				let device = data.devices.find(device => device.is_defaultdevice == true);

				return device && this.devicesMap && this.devicesMap[device.device_id] ? this.devicesMap[device.device_id] : null;
			},	

			
			statusColor(status) {
				return getColor(status)
			},

			toggleFormat(item) {
				this.displayFormat = !this.displayFormat;
				this.formatItem = item;

				if(item) {
					this.selectedItems = [item]
				} else {
					this.selectedItems = []
				}
			},

			toggleReboot(item) {
				this.displayReboot = !this.displayReboot;
				this.rebootDevice = item;
			},

			toggleDeletePrompt(item) {
				this.deleteLogState = false;
				this.deleteTarget = item;
				this.deleteLogRes = null
				this.deleteLogConfirm = !this.deleteLogConfirm;

				if(item) {
					this.selectedItems = [item]
				} else {
					this.selectedItems = []
				}
			},

			closeLogComp() {
				this.showLogs = false;
				this.selectedItems = []
			},

			toggleLogComp(item) {
				this.showLogs = !this.showLogs;
				this.selectedItems = [item]
				this.logItem = item;
			},

			toggleDeleteComp(item) {
				this.showDelete = !this.showDelete;
				this.itemForDeletion = item;
			},

			closeDeleteVehicle() {
				this.showDelete = false;
				this.selectedItems = []
			},

			launchRowAction(e) {
				let allOptions = document.querySelectorAll(".report-action-options");

				for (let option of allOptions) {
					option.classList.remove('active-options');
					if (option === e.target.nextSibling) {
						option.classList.toggle('active-options');
					} 
				}
			},

			handleContactSelection(val) {
				this.showContacts = val;
			},

			handleToggleContacts(context) {
				this.$store.commit('setContactContext', context);
			},

			handleSelection(selection, target) {
				this.selectedContacts = {
					users: selection,
					context: target
				};
			},

			buildTableObj(data) {
				logger.info('buildTableObj ', data)
				const tableData = data.map((item) => ({
					vehicle_name: item.vehicle_name,
					vehicle_type: item.vehicle_type,
					group: this.nodesMap[item.node],
					install_tag: item.devices.length > 0 ? item.devices[0].installation_tag : '',
					vehicle_make: item.vehicle_make,
					vehicle_model: item.vehicle_model,
					vehicle_displacement: item.vehicle_displacement,
					vehicle_year: item.vehicle_year,
					vehicle_fuel: item.vehicle_fuel,
					vehicle_color: item.vehicle_color,
					vehicle_vin: item.vehicle_vin,
					key: item.id,
					id: item.id,
					node: item.node,
					devices: item.devices,
					notes: item.notes
				}));

				return tableData;
			},

			returnVehicleGroup(id, node) {
				let result;

				if (Object.values(node).indexOf(id) > -1) {
					return node;
				} else {
					for (var i = 0; i < node.nodes.length; i += 1) {
						let subNode = node.nodes[i];
						
						result = this.returnVehicleGroup(id, subNode);

						if (result !== false) {
								return result;
						}
					}
					return false;
				}
			},

			async fetchOrgNodes() {
        this.loading = true;
        this.vehicles = [];
				
				const orgTree = await GetOrgTree(this.orgId);
						
				if (orgTree) {
					this.nodes = orgTree;
					this.groups = this.flattenOrgTree([this.nodes.root_node], true)
					this.vehicleGroups = this.flattenOrgTree([this.nodes.root_node]);

					if(this.groups.length > 0) {
						this.selectedGroup = this.groups[0].id
					}
				}
			},

			toggleModal(item) {
				let selectedAmount = this.selectedItems.length;

				if (item) {
					if(!this.selectedItems.includes(item)) {
						this.selectedItems.push(item);
					}
				} else {
					// Clear selection for single select
					// logger.info('testing', selectedAmount, this.displayConfig);
					if (selectedAmount === 1 && this.displayConfig) {
						// logger.info('criteria met')
						this.selectedItems = [];
					}
				}
				this.displayConfig = !this.displayConfig;
			},

			toggleDeviceConfig(item) {
				this.displayDeviceConfig = !this.displayDeviceConfig;
				this.selectedDevice = item;
				this.selectedItems = [item]
			},

			flattenOrgTree(node, returnObject = false) {
				var result = [];
				
				node.forEach((node) => {
					if(returnObject) {
						logger.info('node', node)
						result.push({
							name: node.metadata.label,
							id: node.id
						});
					}
					else {
						logger.info('node', node)
						result.push(node.metadata.label);
					}
						
					if (Array.isArray(node.nodes)) {
						result = result.concat(this.flattenOrgTree(node.nodes, returnObject));
					}
				});
				return result;
			},

			editItem(item) {
				this.editedIndex = this.vehicles.indexOf(item);
				this.editedItem = Object.assign({}, item);
				this.dialog = true;
			},

			async deleteItem() {
				logger.info('deleting vehicle', this.deleteTarget);
				this.deletingItem = true;

				if (this.deleteTarget.devices.length > 0) {
					//unassign devices then delete vehicle
					let deviceObjs = this.deleteTarget.devices;

					for (const device in deviceObjs) {
						let deviceId = deviceObjs[device].device_id;

						await UnassignDeviceFromVehicle(this.orgId, this.deleteTarget["id"], deviceId);
					}
					// There seems to be a delay between unassign a device and being
					// able to delete it. As a work around, we refresh the vehicles 
					// to act as a buffer between operations. 
					const vehicles = await GetOrgVehicles(this.orgId, true);

					if (vehicles && vehicles.length > 0) {
						this.vehicles = this.buildTableObj(vehicles);
						
						let res = await this.deleteVehicle(this.deleteTarget);
						logger.info('what is res? ', res);
						if (res) {
							this.deleteLogState = true;
							this.deleteLogRes = {
								msg: `Vehicle deleted`,
								code: 'green'
							};
						}
						else {
							this.deleteLogState = true;
							this.deleteLogRes = {
								msg: `An error occurred`,
								code: 'red'
							};
						}
					}
				} 
				else {
					let res = await this.deleteVehicle(this.deleteTarget);
					logger.info('what is res? ', res);
					if (res) {
						this.deleteLogState = true;
						this.deleteLogRes = {
							msg: `Vehicle deleted`,
							code: 'green'
						};
					}
					else {
						this.deleteLogState = true;
						this.deleteLogRes = {
							msg: `An error occurred`,
							code: 'red'
						};
					}
				}
			},

			async deleteVehicle(vehicle) {
				const index = this.vehicles.indexOf(vehicle);
				const res = await DeleteOrgVehicle(
					this.orgId,
					vehicle["id"]
				);
				if (res) this.vehicles.splice(index, 1);

				return res;
			},

			close() {
				this.dialog = false;
				this.editedItem = Object.assign({}, this.defaultItem);
				this.editedIndex = -1;
			},

			async save() {
				if (this.editedIndex > -1 && this.$route.params.name) {
					this.loader = true;
					const vehicle = this.vehicles[this.editedIndex];
					const res = await UpdateOrgVehicle(
						this.orgId,
						vehicle["id"],
						this.editedItem
					);
					this.loader = false;
					if (res && res.vehicle_name) {
						Object.assign(vehicle, {
							...vehicle,
							vehicle_name: this.editedItem.vehicle_name,
							vehicle_type: this.editedItem.vehicle_type
						});
						AmplifyEventBus.$emit("snackEvent", {
							color: "success",
							text: `Vehicle ${this.editedItem.vehicleName} updated`
						});
					}
				}
				this.close();
			},

			async createFileRequest(item) {
				logger.info(item)
				let deviceID = item.devices.find(device => {
					return device.is_defaultdevice == true
				});
				const res = await CreateDVRFileRequest(
					this.orgId, item.id, deviceID.device_id,
					{
						file_requests: [
							{
								file_type: 'log',
								file_name: `${item.vehicle_name}_log_${this.date.replace(' ', '_')}`
							}
						],
						description: `${this.date}`
					}
				)
				.then(() => {
					AmplifyEventBus.$emit("snackEvent", {
						color: "success",
						text: `Log file successfully created!`,
					})
				})
				.catch((err) => {
					AmplifyEventBus.$emit("snackEvent", {
						color: "error",
						text: err,
					})
					this.loader = false
					return
				});
				logger.info('called createFileRequest : ', res);
			},

			refreshData() {
				this.fetchVehicleList()
				this.displayVehicleInfo = false
			},

			batchDeviceRequest (id) {
				// eslint-disable-next-line no-async-promise-executor
				return new Promise(async res => {
					try {
						// we need some arbitrary limit to prevent loading forever
						const maxPages = 10
						let currentPage = 0
						let more = true
						let fullList = []
						while(currentPage < maxPages && more) {
							let list = await GetOrgDevices(id, `page_num=${currentPage}&rows_per_page=1000&order_by=id`)
							if(list && list.Devices && Array.isArray(list.Devices) && list.Devices.length) {
								fullList = fullList.concat(list.Devices)

								if(list.Devices.length < 1000) {
									more = false
								}
							} else {
								more = false
							}
							currentPage++
						}
						res({Devices: fullList})
					} catch {
						res([])
					}  
				}) 
			},

			returnInstallTag(data, list) {
				logger.info('returnInstallTag ', data, list)
				const vehicle = list.find(vehicle => vehicle.id == data.id);
				const deviceIds = vehicle ? vehicle.devices : '';
				
				if (deviceIds.length > 0) {
					const device = deviceIds.find(item => item.is_defaultdevice == true);
					const installTag = this.devices.find(item => item.id == device.device_id);
					
					return installTag ? installTag.install_tag : '';

				}
			},

			downloadFile () {
      if (this.vehicles.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new();
        
        // Filter unwanted fields from report
        let cleanedReportCollection = [];


        this.vehicles.forEach(item => {
          let cleanedReportItem = {};
					
          this.headers.forEach(heading => {
            let key = heading.value;

            // eslint-disable-next-line no-prototype-builtins
            if (item.hasOwnProperty(key)) {             
              cleanedReportItem[key] = item[key];
            }
  
            if(!cleanedReportCollection.includes(cleanedReportItem)) {
              cleanedReportCollection.push(cleanedReportItem);
            }
          });
        });

        // create our worksheets
        const report = xlsx.utils.json_to_sheet(cleanedReportCollection);

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, this.reportTitle);

        const reportName = `${this.selectedOrg}_vehicles_list_${moment().format("YYYY-MM-DD HH:mm:ss")}`;
        xlsx.writeFile(wb, reportName + ".xlsx");
      }
    }
		}
	};
</script>

<style lang="scss">
	.vehicles-search-container {
    width: 100%;
    padding-left: 1em;
  }
	.reports-action-launch {
    position: relative;
    cursor: pointer;
  }
	.report-action-options {
    display: none;
  }
	.active-options {
    display: block !important;  
  }
	.middle-align {
		margin-bottom: 40px;
	}
	.info-popup-container {
		border: solid #eeeeee 2px !important;
		background-color: white !important;
		-webkit-box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26); 
		box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26);
	}
	#vehicles-table {
		.v-data-table__wrapper {
			overflow-x: visible !important;
			overflow-y: visible !important;
		}
		.download-container {
			position: absolute;
    	right: 45px;
    	top: 10px;
		}
	}
</style>